import { MessageSquare, Star, TrendingUp, CheckCircle2 } from "lucide-react";

type Filter = "all" | "new" | "qualified" | "converted" | "rejected";

interface EnquiryStatsProps {
  totalCount: number;
  newCount: number;
  qualifiedCount: number;
  convertedCount: number;
  /** The status the list is filtered by; its tile is highlighted */
  active: Filter;
  /** Clicking a tile filters the list to that status */
  onSelect: (status: Filter) => void;
}

export default function EnquiryStats({
  totalCount,
  newCount,
  qualifiedCount,
  convertedCount,
  active,
  onSelect,
}: EnquiryStatsProps) {
  const stats: { key: Filter; label: string; hint: string; count: number; icon: typeof MessageSquare; color: string; bg: string }[] = [
    { key: "all", label: "Total", hint: "All enquiries", count: totalCount, icon: MessageSquare, color: "text-blue-600", bg: "bg-blue-50" },
    { key: "new", label: "New", hint: "Not followed up yet", count: newCount, icon: Star, color: "text-amber-600", bg: "bg-amber-50" },
    { key: "qualified", label: "Qualified", hint: "Ready for a quotation", count: qualifiedCount, icon: TrendingUp, color: "text-purple-600", bg: "bg-purple-50" },
    { key: "converted", label: "Converted", hint: "Quotation saved", count: convertedCount, icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50" },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
      {stats.map((stat) => {
        const Icon = stat.icon;
        const on = active === stat.key;
        return (
          <button
            key={stat.key}
            type="button"
            onClick={() => onSelect(stat.key)}
            aria-pressed={on}
            title={stat.hint}
            className={`text-left rounded-xl p-3 sm:p-4 border transition-colors ${
              on ? "bg-amber-50 border-amber-400" : "bg-white border-stone-200 hover:border-amber-300"
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${stat.bg}`}>
                <Icon className={`w-5 h-5 ${stat.color}`} />
              </div>
              <div className="min-w-0">
                <div className="text-2xl font-bold text-stone-900 tabular-nums">{stat.count}</div>
                <div className="text-xs font-medium text-stone-500 truncate">{stat.label}</div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
